import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Divider, Grid, Theme, Typography, Card, CardHeader, Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Loader, Page, PageTitleWithBack, Button, Table, Link } from '../../../components';
import { getInvoiceDetails } from '../../../fetch';
import { IInvoiceDetail, ILineItem } from '../../../models/invoices';
import { formatDate, formatMoney, formatShortFriendlyDateWithTime } from '../../../helpers';
import ReactToPrint from 'react-to-print';
import { Print } from '@mui/icons-material';

interface IInvoiceDetails {}

export const InvoiceDetails: FC<IInvoiceDetails> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingInvoiceDetails, setIsLoadingInvoiceDetails] = useState<boolean>(true);
  const [invoiceDetails, setInvoiceDetails] = useState<IInvoiceDetail>(null);
  const printRef = useRef();

  const { invoiceNumber }: { invoiceNumber: string } = useParams();
  const classes = useStyles();

  const fetchInvoiceDetails = async () => {
    try {
      setIsLoadingInvoiceDetails(true);
      const res = await getInvoiceDetails(invoiceNumber);
      if (res?.invoiceNumber) {
        setInvoiceDetails(res);
      } else if (res?.Detail) {
        enqueueSnackbar(res?.Detail, {
          variant: 'error'
        });
      } else {
        enqueueSnackbar(`Something went wrong. please refresh and try again`, {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Something went wrong. ${error}`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingInvoiceDetails(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lineItemColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name'
      },
      {
        Header: 'Price Per Unit',
        accessor: 'pricePerUnit',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ILineItem } };
        }) => {
          return <span>{formatMoney(original.pricePerUnit)}</span>;
        }
      },
      {
        Header: 'Quantity',
        accessor: 'quantity'
      },
      {
        Header: 'Total',
        accessor: 'totalCost',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ILineItem } };
        }) => {
          return <span>{formatMoney(original.totalCost)}</span>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDetails]);

  return (
    <Page
      title={`Invoice Details${
        invoiceDetails?.invoiceNumber ? ` for ${invoiceDetails?.invoiceNumber}` : ''
      }`}
      showSideBar={false}
      sideBarLinks={[]}
    >
      <Grid container className={classes.pageContainer}>
        <Grid item xs={12} md={7} className={classes.pageTitleWrapper}>
          <PageTitleWithBack
            showDivider={false}
            backText={'Back to Invoices'}
            backUrl={'/vendors/invoices'}
            title={`Invoice Details${
              invoiceDetails?.invoiceNumber ? ` for ${invoiceDetails?.invoiceNumber}` : ''
            }`}
          />
        </Grid>
        <Grid item xs={12} md={4} className={classes.dateWrapper}>
          <Typography className={classes.lastViewed}>
            {invoiceDetails && invoiceDetails.dateLastViewed
              ? `Last Viewed: ${formatShortFriendlyDateWithTime(invoiceDetails.dateLastViewed)}`
              : ''}
          </Typography>
        </Grid>
        <Grid item xs={12} md={1} className={classes.infoWrapper}>
          {invoiceDetails && (
            <ReactToPrint
              trigger={() => (
                <Button
                  id="Print"
                  startIcon={<Print />}
                  type="button"
                  color="primary"
                  variant="contained"
                >
                  Print
                </Button>
              )}
              content={() => printRef.current}
            />
          )}
        </Grid>
      </Grid>
      <Divider />
      <Grid
        spacing={2}
        container
        alignItems="center"
        justifyContent="space-around"
        className={classes.pageWrapper}
      >
        {isLoadingInvoiceDetails && <Loader type="overlay" position="centered" />}
        {!isLoadingInvoiceDetails && !invoiceDetails && (
          <Box
            display="flex"
            height="10rem"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography>Invoice does not exist</Typography>
            <Link to="/vendors/invoices">Back to invoices</Link>
          </Box>
        )}
        {invoiceDetails && (
          <>
            <Grid ref={printRef} item xs={12} lg={6}>
              <Card variant={'outlined'} className={classes.invoiceCard}>
                <CardHeader title={invoiceDetails.vendor} />
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <Typography color="primary">Invoice Number</Typography>
                    <Typography variant="h6" component="div">
                      {invoiceDetails.invoiceNumber}
                    </Typography>
                    <Typography color="primary">Date Submitted</Typography>
                    <Typography variant="h6" component="div">
                      {formatDate(invoiceDetails.dateInvoice)}
                    </Typography>
                    <Typography color="primary">Date to be Paid Out</Typography>
                    <Typography variant="h6" component="div">
                      {formatDate(invoiceDetails.dateDue)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color="primary">Status</Typography>
                    <Typography variant="h6" component="div">
                      {invoiceDetails.status}
                    </Typography>
                    <Typography color="primary">Check Number</Typography>
                    <Typography variant="h6" component="div">
                      {invoiceDetails.payments && invoiceDetails.payments.length < 1
                        ? null
                        : invoiceDetails.payments.map((payment, index) => {
                            const isACH = payment.checkNumber.includes('ACH ');
                            return (
                              <Link
                                key={`${index}-${payment.checkNumber}`}
                                className={classes.link}
                                to={`/vendors/payments/${payment.checkNumber}?type=${
                                  isACH ? 'ACHPayment' : 'Check'
                                }&vendorId=${invoiceDetails.vendorId}`}
                              >
                                {payment.checkNumber}
                              </Link>
                            );
                          })}
                    </Typography>
                    <Typography color="primary">Job</Typography>
                    <Typography variant="h6" component="div">
                      {invoiceDetails.job}
                    </Typography>
                  </Grid>
                  <Grid className={classes.lineItems} item xs={12}>
                    <>
                      <Table
                        columns={lineItemColumns}
                        data={invoiceDetails.lineItems}
                        isLoading={isLoadingInvoiceDetails}
                        hidePagination
                      />
                      <div className={classes.total}>
                        <Typography color="primary">Total</Typography>
                        <Typography variant="h6" component="div">
                          {formatMoney(invoiceDetails.total)}
                        </Typography>
                      </div>
                    </>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    justifyContent: 'space-around',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  wrapper: {
    marginTop: '2rem'
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  link: {
    textDecoration: 'none'
  },
  infoWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 900px)': {
      textAlign: 'right'
    }
  },
  dateWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 600px)': {
      textAlign: 'center'
    }
  },
  pageWrapper: {
    marginTop: 0
  },
  cardRoot: {},
  content: {
    padding: 24,
    textAlign: 'center'
  },
  pageContainer: {
    justifyContent: 'space-around',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'normal'
    }
  },
  selectedTradePartner: {
    textAlign: 'left',
    minWidth: '150px',
    maxWidth: '100%',
    marginBottom: theme.spacing(0.5)
  },
  invoiceCard: {
    padding: theme.spacing(1)
  },
  lineItems: {
    marginTop: theme.spacing(1)
  },
  total: {
    padding: theme.spacing(1),
    textAlign: 'right'
  },
  lastViewed: {
    fontWeight: theme.typography.fontWeightLight,
    paddingTop: theme.spacing(0.5),
    fontStyle: 'italic'
  }
}));
